import React, { useState } from 'react'
import { useFence, FenceSystem, FenceElement } from '../contexts/FenceContext'
import { getFenceConfigWithColor, getFenceConfig } from '../config/fenceConfigs'
import {
  HouseStyleSelector,
  ElementToolbar,
  Canvas2D,
  ThreeJSCanvas3D,
  ElementProperties,
  FenceSelectionPopup
} from '.'
import PDFGenerator from './PDFGenerator'
import QuotePreview from './QuotePreview'

const DEBUG_LOGS = false

const FenceConfigurator: React.FC = () => {
  const { state, dispatch, addPost, addSection, addGate, addDoor, closePerimeter } = useFence()
  const [variantFenceConfig, setVariantFenceConfig] = React.useState<any>(null)
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null)
  const [resetCounter, setResetCounter] = useState(0)
  const [showLeftSidebar, setShowLeftSidebar] = useState(false)
  const [showRightSidebar, setShowRightSidebar] = useState(false)
  const [hasStartPoint, setHasStartPoint] = useState(false)
  const [showStartPointPopup, setShowStartPointPopup] = useState(false)
  const [showFencePopup, setShowFencePopup] = useState(false)
  const [fullFenceConfig, setFullFenceConfig] = useState<any>(null)
  const [isMobile, setIsMobile] = useState(false)
  
  // 🎯 Sprawdź czy jesteśmy na mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(typeof window !== 'undefined' && window.innerWidth < 1024)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  // 🎯 Mobile helper functions - używają tej samej logiki co ElementToolbar
  const handleMobileAddPost = React.useCallback(async () => {
    if (state.isClosed || !fullFenceConfig) return
    
    const fenceConfig = fullFenceConfig
    const selectedVariant = state.selectedVariant || (fullFenceConfig?.availableVariants?.[0])
    const isFullFence = (fullFenceConfig?.availableVariants?.length ?? 0) > 0
    
    let position: { x: number; y: number; z: number }
    if ((window as any).getNextElementPosition) {
      try {
        const result = (window as any).getNextElementPosition(fenceConfig, state.elements, 'post', selectedVariant)
        position = result?.position || { x: state.elements.length * 2.0, y: 0, z: 0 }
      } catch {
        position = { x: state.elements.length * 2.0, y: 0, z: 0 }
      }
    } else {
      position = { x: state.elements.length * 2.0, y: 0, z: 0 }
    }
    
    const postHeight = 6
    const decorationBlocksCount = fenceConfig?.elements?.post?.decorationBlocksCount || 1
    const totalHeight = postHeight + decorationBlocksCount
    
    if ((window as any).addFenceElement) {
      const tempPostId = `temp-post-${Date.now()}`
      await (window as any).addFenceElement(
        tempPostId, 'post', position, fenceConfig, totalHeight, state.selectedColor,
        async (receivedElementId: string, _pos: any, dimensions: any) => {
          const postElementId = await addPost(position, { ...dimensions, height: totalHeight }, isFullFence ? selectedVariant : undefined)
          if ((window as any).updateElementId) {
            (window as any).updateElementId(tempPostId, postElementId)
          }
        },
        undefined, isFullFence ? selectedVariant : undefined
      )
    } else {
      await addPost(position, undefined, isFullFence ? selectedVariant : undefined)
    }
  }, [state, fullFenceConfig, addPost])
  
  const handleMobileAddSection = React.useCallback(async () => {
    if (state.isClosed || !fullFenceConfig) return
    
    const fenceConfig = fullFenceConfig
    const selectedVariant = state.selectedVariant || (fullFenceConfig?.availableVariants?.[0])
    const isFullFence = (fullFenceConfig?.availableVariants?.length ?? 0) > 0
    
    let position: { x: number; y: number; z: number }
    if ((window as any).getNextElementPosition) {
      try {
        const result = (window as any).getNextElementPosition(fenceConfig, state.elements, 'section', selectedVariant)
        position = result?.position || { x: state.elements.length * 2.0, y: 0, z: 0 }
      } catch {
        position = { x: state.elements.length * 2.0, y: 0, z: 0 }
      }
    } else {
      position = { x: state.elements.length * 2.0, y: 0, z: 0 }
    }
    
    let defaultWidthMultiplier = 1
    if (fenceConfig?.id?.startsWith('lupane-muro-')) {
      defaultWidthMultiplier = 3
    } else if (fenceConfig?.id?.startsWith('royal')) {
      defaultWidthMultiplier = 2
    }
    
    const elementId = await addSection(position, 2.0, undefined, isFullFence ? selectedVariant : undefined, defaultWidthMultiplier)
    
    if ((window as any).addFenceElement) {
      await (window as any).addFenceElement(
        elementId, 'section', position, fenceConfig, undefined, state.selectedColor,
        (receivedElementId: string, _pos: any, dimensions: any) => {
          // Update width only
        },
        defaultWidthMultiplier, isFullFence ? selectedVariant : undefined
      )
    }
  }, [state, fullFenceConfig, addSection])
  
  const handleMobileAddGate = React.useCallback(async () => {
    if (state.isClosed || !fullFenceConfig) return
    
    const fenceConfig = fullFenceConfig
    const selectedVariant = state.selectedVariant || (fullFenceConfig?.availableVariants?.[0])
    const isFullFence = (fullFenceConfig?.availableVariants?.length ?? 0) > 0
    const widthMultiplier = 2
    
    let position: { x: number; y: number; z: number }
    if ((window as any).getNextElementPosition) {
      try {
        ;(window as any).__nextElementWidthMultiplier = widthMultiplier
        const result = (window as any).getNextElementPosition(fenceConfig, state.elements, 'gate', selectedVariant)
        position = result?.position || { x: state.elements.length * 2.0, y: 0, z: 0 }
      } catch {
        position = { x: state.elements.length * 2.0, y: 0, z: 0 }
      }
    } else {
      position = { x: state.elements.length * 2.0, y: 0, z: 0 }
    }
    
    const elementId = await addGate(position, undefined, isFullFence ? selectedVariant : undefined)
    
    if ((window as any).addFenceElement) {
      await (window as any).addFenceElement(
        elementId, 'gate', position, fenceConfig, undefined, state.selectedColor,
        () => {}, widthMultiplier, selectedVariant
      )
    }
  }, [state, fullFenceConfig, addGate])
  
  const handleMobileAddDoor = React.useCallback(async () => {
    if (state.isClosed || !fullFenceConfig) return
    
    const fenceConfig = fullFenceConfig
    const selectedVariant = state.selectedVariant || (fullFenceConfig?.availableVariants?.[0])
    const isFullFence = (fullFenceConfig?.availableVariants?.length ?? 0) > 0
    const defaultDoorWidth = (fenceConfig?.id?.includes('royal')) ? 2 : 1
    
    let position: { x: number; y: number; z: number }
    if ((window as any).getNextElementPosition) {
      try {
        ;(window as any).__nextElementWidthMultiplier = defaultDoorWidth
        const result = (window as any).getNextElementPosition(fenceConfig, state.elements, 'door', selectedVariant)
        position = result?.position || { x: state.elements.length * 2.0, y: 0, z: 0 }
      } catch {
        position = { x: state.elements.length * 2.0, y: 0, z: 0 }
      }
    } else {
      position = { x: state.elements.length * 2.0, y: 0, z: 0 }
    }
    
    const elementId = await addDoor(position, undefined, isFullFence ? selectedVariant : undefined)
    
    if ((window as any).addFenceElement) {
      await (window as any).addFenceElement(
        elementId, 'door', position, fenceConfig, undefined, state.selectedColor,
        () => {}, defaultDoorWidth, selectedVariant
      )
    }
  }, [state, fullFenceConfig, addDoor])
  
  const handleMobileClosePerimeter = React.useCallback(async () => {
    if (!fullFenceConfig || state.elements.length < 2) return
    
    const fenceConfig = fullFenceConfig
    const selectedVariant = state.selectedVariant || (fullFenceConfig?.availableVariants?.[0])
    const isFullFence = (fullFenceConfig?.availableVariants?.length ?? 0) > 0
    
    if ((window as any).closeFencePerimeter) {
      const result = await (window as any).closeFencePerimeter(fenceConfig, state.selectedColor, isFullFence ? selectedVariant : undefined)
      if (result?.success) {
        closePerimeter()
        if (result.addedSection) {
          await addSection(
            result.addedSection.position,
            result.addedSection.dimensions.width,
            undefined,
            isFullFence ? selectedVariant : undefined,
            result.addedSection.widthMultiplier,
            true
          )
        }
      }
    }
  }, [state, fullFenceConfig, closePerimeter, addSection])

  // Set default sidebar state based on screen size
  React.useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        if (window.innerWidth >= 768) {
          setShowLeftSidebar(true)
        }
        if (window.innerWidth >= 1024) {
          setShowRightSidebar(true)
        }
      }
    }
    
    handleResize() // Set initial state
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Debug logging
  React.useEffect(() => {
    console.log('🔍 Debug - viewMode:', state.viewMode)
    console.log('🔍 Debug - renderuję komponent:', state.viewMode === '3D' ? 'ThreeJSCanvas3D' : 'Canvas2D')
    console.log('🎨 Debug - selectedColor:', state.selectedColor)
    console.log('🎨 Debug - selectedSystem:', state.selectedSystem?.name)
    console.log('🔍 Debug - 3D canvas visible:', state.viewMode !== '2D')
    console.log('🔍 Debug - 2D canvas visible:', state.viewMode === '2D')
    console.log('📱 Debug - showLeftSidebar:', showLeftSidebar)
    console.log('📱 Debug - showRightSidebar:', showRightSidebar)
    console.log('📱 Debug - window width:', typeof window !== 'undefined' ? window.innerWidth : 'SSR')
  }, [state.viewMode, state.selectedColor, state.selectedSystem, showLeftSidebar, showRightSidebar])

  // 🎯 ŁADUJ PEŁNĄ KONFIGURACJĘ Z JSON
  React.useEffect(() => {
    const loadFullConfig = async () => {
      if (state.selectedSystem) {
        try {
          const config = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
          setFullFenceConfig(config)
          console.log('🎯 Załadowano pełną konfigurację:', config?.name, 'elements:', config?.elements)
        } catch (error) {
          console.error('❌ Błąd ładowania konfiguracji:', error)
        }
      } else {
        setFullFenceConfig(null)
      }
    }
    loadFullConfig()
  }, [state.selectedSystem, state.selectedColor])

  // 🎯 ŁADUJ KONFIGURACJĘ WARIANTU (dla wyświetlania cen)
  React.useEffect(() => {
    const loadVariantConfig = async () => {
      if (state.selectedSystem && fullFenceConfig?.variantConfigs) {
        // 🎯 Jeśli jest wybrany wariant, użyj go
        if (state.selectedVariant && fullFenceConfig.variantConfigs[state.selectedVariant]) {
          try {
            const variantConfigId = fullFenceConfig.variantConfigs[state.selectedVariant]
            const variantConfig = await getFenceConfigWithColor(variantConfigId, state.selectedColor || undefined)
            setVariantFenceConfig(variantConfig)
            DEBUG_LOGS && console.log('🎯 Załadowano konfigurację wariantu:', variantConfig?.name, 'dla wariantu:', state.selectedVariant, 'blockPrice:', variantConfig?.blockPrice, 'decorationBlockPrice:', variantConfig?.decorationBlockPrice)
          } catch (error) {
            console.error('❌ Błąd ładowania konfiguracji wariantu:', error)
            setVariantFenceConfig(null)
          }
        } 
        // 🎯 Jeśli nie ma wybranego wariantu, ale system ma dostępne warianty (np. Royal Mix, Muro Mix),
        // załaduj domyślny wariant dla wyświetlania cen
        else if (fullFenceConfig.availableVariants && fullFenceConfig.availableVariants.length > 0) {
          try {
            // 🎯 Dla Royal Mix: preferuj P50 jako domyślny wariant (jeśli dostępny)
            // Dla innych systemów: użyj pierwszego dostępnego wariantu
            let defaultVariant: string | undefined
            if (fullFenceConfig.id === 'royal-mix' && fullFenceConfig.availableVariants.includes('p50')) {
              defaultVariant = 'p50'
            } else {
              defaultVariant = fullFenceConfig.availableVariants[0] // Pierwszy dostępny wariant jako domyślny
            }
            
            const variantConfigId = defaultVariant ? fullFenceConfig.variantConfigs[defaultVariant] : undefined
            if (variantConfigId) {
              const variantConfig = await getFenceConfigWithColor(variantConfigId, state.selectedColor || undefined)
              setVariantFenceConfig(variantConfig)
              DEBUG_LOGS && console.log('🎯 Załadowano domyślną konfigurację wariantu:', variantConfig?.name, 'dla wariantu:', defaultVariant, 'blockPrice:', variantConfig?.blockPrice, 'decorationBlockPrice:', variantConfig?.decorationBlockPrice)
            } else {
              setVariantFenceConfig(null)
            }
          } catch (error) {
            console.error('❌ Błąd ładowania domyślnej konfiguracji wariantu:', error)
            setVariantFenceConfig(null)
          }
        } else {
          setVariantFenceConfig(null)
        }
      } else {
        setVariantFenceConfig(null)
      }
    }
    loadVariantConfig()
  }, [state.selectedSystem, state.selectedVariant, state.selectedColor, fullFenceConfig])


  // Obsługa eventów z 3D canvas
  React.useEffect(() => {
    const handleToggleLeftSidebar = () => setShowLeftSidebar(!showLeftSidebar)
    const handleToggleRightSidebar = () => setShowRightSidebar(!showRightSidebar)
    const handleToggleViewMode = () => {
      dispatch({ type: 'SET_VIEW_MODE', payload: state.viewMode === '3D' ? '2D' : '3D' })
    }

    window.addEventListener('toggleLeftSidebar', handleToggleLeftSidebar)
    window.addEventListener('toggleRightSidebar', handleToggleRightSidebar)
    window.addEventListener('toggleViewMode', handleToggleViewMode)

    return () => {
      window.removeEventListener('toggleLeftSidebar', handleToggleLeftSidebar)
      window.removeEventListener('toggleRightSidebar', handleToggleRightSidebar)
      window.removeEventListener('toggleViewMode', handleToggleViewMode)
    }
  }, [showLeftSidebar, showRightSidebar, state.viewMode, dispatch])

  const handleReset = () => {
    dispatch({ type: 'RESET_PROJECT' })
    setSelectedElementId(null)
    setResetCounter(prev => prev + 1)
    setHasStartPoint(false)
    setShowStartPointPopup(false)
  }

  // 🎯 Reaguj na wybór punktu startu (z 3D lub 2D)
  React.useEffect(() => {
    const handleStartPointSelected = () => {
      setHasStartPoint(true)
      setShowStartPointPopup(false)
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('fenceStartPointSelected', handleStartPointSelected)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('fenceStartPointSelected', handleStartPointSelected)
      }
    }
  }, [])
  
  // 🎯 DEBUG: Handler dla wyboru elementu
  const handleElementSelect = (elementId: string) => {
    console.log(`🎯 FenceConfigurator: handleElementSelect wywołane z elementId: ${elementId}`)
    console.log(`🎯 Poprzedni selectedElementId: ${selectedElementId}`)
    
    // 🚫 Dla sekcji zamykającej (ID zaczyna się od "closing-") - nie pokazuj właściwości
    if (elementId && elementId.startsWith('closing-')) {
      console.log(`🚫 FenceConfigurator: Sekcja zamykająca wykryta - czyszczę wybór`)
      setSelectedElementId(null)
      return
    }
    
    setSelectedElementId(elementId)
    console.log(`🎯 Nowy selectedElementId: ${elementId}`)
    
    // 🎯 Na mobile: automatycznie otwórz prawy sidebar z właściwościami elementu
    if (typeof window !== 'undefined' && window.innerWidth < 1024 && elementId) {
      setShowRightSidebar(true)
    }
  }
  
  // 🎯 Automatycznie otwórz prawy sidebar na mobile gdy element jest wybrany
  React.useEffect(() => {
    if (selectedElementId && typeof window !== 'undefined' && window.innerWidth < 1024) {
      setShowRightSidebar(true)
    }
  }, [selectedElementId])

  const handleFenceSelect = async (fence: FenceSystem | null) => {
    if (fence) {
      // 🎯 Wyczyść wszystkie elementy z poprzedniego systemu
      console.log('🧹 Czyszczenie sceny przed zmianą systemu ogrodzenia...')
      if (typeof window !== 'undefined' && (window as any).clearAllFenceElements) {
        (window as any).clearAllFenceElements()
      }
      // Resetuj stan React (wszystkie elementy)
      dispatch({ type: 'CLEAR_ELEMENTS' })
      
      // 🚀 PRELOADING: Załaduj model i tekstury w tle od razu po wyborze systemu
      // Dzięki temu gdy użytkownik kliknie "dodaj element", wszystko będzie już w cache
      // ⚠️ Nie ładuj dla koloru "default" - preloadFenceAssets sprawdzi czy kolor istnieje
      import('../utils/threejs/modelLoader').then(({ preloadFenceAssets }) => {
        if (state.selectedColor) {
          preloadFenceAssets(fence.id, state.selectedColor)
        }
        // Jeśli nie ma wybranego koloru, nie ładuj - użytkownik musi najpierw wybrać kolor
      })
      
      // Ustaw nowy system
      dispatch({ type: 'SELECT_SYSTEM', payload: fence })
      console.log('🎯 Wybrano ogrodzenie:', fence)

      // 🎯 Po wyborze ogrodzenia: wymagaj wskazania punktu startowego na planszy
      // ⚠️ TYLKO jeśli nie ma jeszcze żadnych elementów w projekcie
      if (typeof window !== 'undefined' && state.elements.length === 0) {
        ;(window as any).__fenceStartPoint = null
        ;(window as any).__isSelectingStartPoint = true
        console.log('📍 Oczekiwanie na wybór punktu startu ogrodzenia – kliknij na planszy')

        // 🎯 Zmień kursor na "celownik" nad canvaseem 3D
        const canvasContainer = document.querySelector('.threejs-canvas') as HTMLDivElement | null
        if (canvasContainer) {
          const canvas = canvasContainer.querySelector('canvas') as HTMLCanvasElement | null
          if (canvas) {
            canvas.style.cursor = 'crosshair'
          }
        }
        
        // 🎯 Pokaż popup tylko jeśli nie ma jeszcze elementów
        if (state.elements.length === 0) {
          setShowStartPointPopup(true)
        }
      }

      // Resetuj status punktu startu po wyborze nowego ogrodzenia i pokaż dedykowany popup
      // ⚠️ TYLKO jeśli nie ma jeszcze żadnych elementów w projekcie
      setHasStartPoint(false)
      if (state.elements.length === 0) {
        setShowStartPointPopup(true)
      }

      setShowFencePopup(false)
      setSelectedElementId(null) // Resetuj wybór elementu
    }
  }



  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Navigation Bar */}
      <div className="md:hidden bg-white border-b border-gray-200 sticky top-0 z-20 touch-manipulation shadow-sm">
        {/* Top row - Main navigation */}
        <div className="px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowLeftSidebar(!showLeftSidebar)}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors touch-manipulation mobile-button"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="font-sans text-lg font-semibold text-gray-900">Konfigurator</h1>
          </div>
          <button
            onClick={() => {
              setShowRightSidebar(!showRightSidebar)
            }}
            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors touch-manipulation mobile-button relative"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {/* Badge pokazujący czy element jest wybrany */}
            {selectedElementId && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>
            )}
          </button>
        </div>

        {/* Bottom row - View mode toggle and quick actions */}
        <div className="px-3 sm:px-4 pb-2 sm:pb-3">
          <div className="flex items-center justify-between space-x-3">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => {
                  console.log('🔄 Switching to 2D mode')
                  dispatch({ type: 'SET_VIEW_MODE', payload: '2D' })
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors touch-manipulation mobile-button ${
                  state.viewMode === '2D'
                    ? 'bg-white text-red-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                2D
              </button>
              <button
                onClick={() => {
                  console.log('🔄 Switching to 3D mode')
                  dispatch({ type: 'SET_VIEW_MODE', payload: '3D' })
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors touch-manipulation mobile-button ${
                  state.viewMode === '3D'
                    ? 'bg-white text-red-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                3D
              </button>
            </div>

            {/* Mobile element controls */}
            {state.selectedSystem && (
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowLeftSidebar(true)}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-200 transition-colors touch-manipulation mobile-button"
                >
                  ⚙️ Opcje
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Full Screen Layout */}
      <div className="flex h-[calc(100vh-112px)] md:h-[calc(100vh-64px)] relative" style={{ paddingBottom: state.selectedSystem ? (typeof window !== 'undefined' && window.innerWidth < 768 ? '180px' : '0px') : '0px' }}>
        {/* Overlay dla mobilnych sidebarów */}
        {((showLeftSidebar && typeof window !== 'undefined' && window.innerWidth < 768) || (showRightSidebar && typeof window !== 'undefined' && window.innerWidth < 1024)) && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden mobile-overlay"
            onClick={() => {
              setShowLeftSidebar(false)
              setShowRightSidebar(false)
            }}
          />
        )}

        {/* Left Sidebar - Fence Selection & Controls */}
        <div className={`${showLeftSidebar ? 'block' : 'hidden'} md:block w-full sm:w-80 lg:w-96 bg-white border-r border-gray-200 overflow-y-auto fixed md:relative z-40 md:z-auto h-full shadow-lg touch-manipulation mobile-sidebar ${showLeftSidebar ? 'open' : ''}`}>
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Kompaktowy widok wybranego ogrodzenia */}
            {state.selectedSystem ? (
              <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm space-y-3">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <h3 className="font-sans text-sm sm:text-base font-semibold text-gray-900">Wybrane ogrodzenie</h3>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-sans touch-manipulation"
                  >
                    Zmień
                  </button>
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="font-sans text-gray-600">Typ:</span>
                    <span className="font-sans font-medium text-gray-900 truncate ml-2">{state.selectedSystem.name}</span>
                  </div>
                  {state.selectedColor && (
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="font-sans text-gray-600">Kolor:</span>
                      <span className="font-sans font-medium text-gray-900">{state.selectedColor}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="font-sans text-gray-600">Wymiary bloczka:</span>
                    <span className="font-sans font-medium text-gray-900">{state.selectedSystem.dimensions || 'Standardowe'}</span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="font-sans text-gray-600">Cena bloczka:</span>
                    <span className="font-sans font-medium text-gray-900">
                      {(() => {
                        // 🎯 Użyj konfiguracji wariantu, jeśli jest dostępna, w przeciwnym razie użyj głównej konfiguracji
                        const configToUse = variantFenceConfig || fullFenceConfig
                        // 🎯 WAŻNE: Najpierw sprawdź cenę z konfiguracji wariantu/głównej, potem z selectedSystem
                        let blockPrice = configToUse?.blockPrice ?? state.selectedSystem?.blockPrice ?? 150
                        if (state.selectedColor && configToUse?.colorPrices?.[state.selectedColor]?.blockPrice) {
                          blockPrice = configToUse.colorPrices[state.selectedColor].blockPrice!
                        }
                        return blockPrice.toFixed(2)
                      })()} zł
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="font-sans text-gray-600">Cena bloczka dekoracyjnego:</span>
                    <span className="font-sans font-medium text-gray-900">
                      {(() => {
                        // 🎯 Użyj konfiguracji wariantu, jeśli jest dostępna, w przeciwnym razie użyj głównej konfiguracji
                        const configToUse = variantFenceConfig || fullFenceConfig
                        // 🎯 WAŻNE: Najpierw sprawdź cenę z konfiguracji wariantu/głównej, potem z selectedSystem
                        let decorationBlockPrice = 
                          configToUse?.decorationBlockPrice ?? 
                          state.selectedSystem?.decorationBlockPrice ?? 
                          configToUse?.blockPrice ?? 
                          state.selectedSystem?.blockPrice ?? 
                          150
                        if (state.selectedColor && configToUse?.colorPrices?.[state.selectedColor]?.decorationBlockPrice) {
                          decorationBlockPrice = configToUse.colorPrices[state.selectedColor].decorationBlockPrice!
                        }
                        return decorationBlockPrice.toFixed(2)
                      })()} zł
                    </span>
                  </div>
                </div>

                {/* 🎯 Status punktu startu */}
                {hasStartPoint && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-sans text-sm text-green-700 font-medium">
                      Punkt startu ogrodzenia został wybrany – możesz dodawać elementy.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <p className="font-sans text-sm sm:text-base text-red-700 mb-2 sm:mb-3">Nie wybrano systemu ogrodzenia</p>
                <button
                  onClick={() => setShowFencePopup(true)}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-sans font-medium text-sm sm:text-base touch-manipulation"
                >
                  Wybierz ogrodzenie
                </button>
              </div>
            )}
            
            {/* House Style */}
            <HouseStyleSelector />
            
            {/* Element Toolbar */}
            <ElementToolbar onElementSelect={setSelectedElementId} selectedFence={state.selectedSystem} fullFenceConfig={fullFenceConfig} />
          </div>
        </div>

        {/* Center - 3D Canvas */}
        <div className="flex-1 bg-gray-50 relative min-h-[300px] md:min-h-[400px] touch-manipulation w-full">
          
          {!state.selectedSystem ? (
            <div className="h-full w-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">🏠</span>
                </div>
                <h3 className="font-sans text-lg font-semibold text-gray-900 mb-2">Wybierz system ogrodzenia</h3>
                <p className="font-sans text-sm text-gray-600 mb-4">
                  Aby rozpocząć konfigurację, wybierz typ ogrodzenia z lewego menu
                </p>
                <button
                  onClick={() => setShowFencePopup(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-sans font-medium touch-manipulation mobile-button"
                >
                  Wybierz ogrodzenie
                </button>
              </div>
            </div>
          ) : (
            <div className="h-full w-full">
              {/* 🎯 NAPRAWKA: Zawsze renderuj ThreeJSCanvas3D, ale ukryj gdy viewMode === '2D' */}
              <div className={`h-full w-full ${state.viewMode === '2D' ? 'hidden' : 'block'}`}>
                <div className="threejs-canvas h-full w-full">
                  <ThreeJSCanvas3D
                    elements={state.elements}
                    selectedElementId={selectedElementId}
                    onElementSelect={handleElementSelect}
                    fenceSystem={state.selectedSystem}
                    houseStyle={state.selectedHouseStyle}
                    onReset={() => handleReset()}
                    selectedFence={fullFenceConfig || state.selectedSystem}
                    viewMode={state.viewMode}
                    resetCounter={resetCounter}
                    selectedColor={state.selectedColor} // 🎨 Przekaż wybrany kolor
                  />
                </div>
              </div>
              
              {/* 🎯 NAPRAWKA: Canvas2D tylko gdy viewMode === '2D' */}
              {state.viewMode === '2D' && (
                <div className="h-full w-full">
                  <Canvas2D
                    elements={state.elements}
                    selectedElementId={selectedElementId}
                    onElementSelect={setSelectedElementId}
                    fenceSystem={state.selectedSystem}
                    onReset={() => handleReset()}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Sidebar - Properties & Info */}
        <div className={`${showRightSidebar ? 'block' : 'hidden'} lg:block w-full sm:w-80 lg:w-96 xl:w-[28rem] bg-white border-l border-gray-200 fixed right-0 lg:relative z-40 md:z-auto ${selectedElementId && isMobile ? 'max-h-[70vh] top-auto bottom-0' : isMobile ? 'h-[calc(100vh-112px)] top-[112px]' : 'h-full'} shadow-lg touch-manipulation mobile-sidebar ${showRightSidebar ? 'open' : ''} overflow-y-auto`}>
          {/* Header prawej sekcji - z przyciskiem zamknięcia na mobile */}
          <div className={`${selectedElementId && isMobile ? 'p-2.5' : 'p-4 sm:p-6'} border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between flex-shrink-0`}>
            <div className="flex-1 min-w-0">
              <h2 className={`font-sans ${selectedElementId && isMobile ? 'text-xs' : 'text-sm sm:text-base'} font-semibold text-gray-900`}>
                {selectedElementId ? 'Właściwości elementu' : 'Panel sterowania'}
              </h2>
              {(!selectedElementId || !isMobile) && (
                <p className="font-sans text-xs sm:text-sm text-gray-600 mt-1">
                  {selectedElementId ? 'Edytuj wybrany element' : 'Właściwości i podsumowanie projektu'}
                </p>
              )}
            </div>
            {/* Przycisk zamknięcia na mobile */}
            <button
              onClick={() => {
                setShowRightSidebar(false)
                // Na mobile: gdy zamykamy sidebar, wyczyść wybór elementu
                if (isMobile) {
                  setSelectedElementId(null)
                }
              }}
              className="lg:hidden p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors touch-manipulation mobile-button flex-shrink-0 ml-2"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className={`${selectedElementId && isMobile ? 'p-2.5' : 'p-4 sm:p-6'} ${isMobile ? 'pb-32' : 'pb-6'} space-y-4 sm:space-y-6`}>
            {selectedElementId && (() => {
              console.log(`🎯 FenceConfigurator: Szukam elementu ${selectedElementId}`)
              console.log(`🎯 FenceConfigurator: Wszystkie elementy w stanie:`, state.elements)
              console.log(`🎯 FenceConfigurator: Typ selectedElementId:`, typeof selectedElementId)
              console.log(`🎯 FenceConfigurator: Typy ID elementów:`, state.elements.map(el => ({ id: el.id, idType: typeof el.id, type: el.type })))
              
              const selectedElement = state.elements.find(el => el.id === selectedElementId)
              console.log(`🎯 FenceConfigurator: Znaleziony element:`, selectedElement)
              console.log(`🎯 FenceConfigurator: Element widthMultiplier:`, selectedElement?.widthMultiplier)
              
              return selectedElement ? (
                <ElementProperties
                  key={`${selectedElement.id}-${selectedElement.widthMultiplier || 1}`} // 🎯 Resetuj komponent gdy zmienia się element LUB widthMultiplier
                  element={selectedElement}
                  onUpdate={(updates) => {
                    dispatch({ type: 'UPDATE_ELEMENT', payload: { id: selectedElementId, updates } })
                  }}
                  onDelete={() => {
                    // Usuń z 3D sceny
                    if ((window as any).removeElement) {
                      (window as any).removeElement(selectedElementId)
                    }
                    
                    // Usuń z React state
                    dispatch({ type: 'REMOVE_ELEMENT', payload: selectedElementId })
                    setSelectedElementId(null)
                  }}
                />
              ) : (
                // 🚫 Dla sekcji zamykającej (ID zaczyna się od "closing-") - nie pokazuj wiadomości błędu
                selectedElementId && selectedElementId.startsWith('closing-') ? null : (
                  <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-full mx-auto mb-2 sm:mb-3 flex items-center justify-center">
                      <span className="text-lg sm:text-xl">⚠️</span>
                    </div>
                    <h3 className="font-sans text-sm sm:text-base font-semibold text-gray-900 mb-2">Element nie został znaleziony</h3>
                    <p className="font-sans text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                      Wybrany element może zostać usunięty lub zmieniony
                    </p>
                    <button 
                      onClick={() => setSelectedElementId(null)}
                      className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-sans font-medium text-xs sm:text-sm touch-manipulation mobile-button"
                    >
                      Wyczyść wybór
                    </button>
                  </div>
                )
              )
            })()}
            
            {/* Instrukcje gdy brak wybranego elementu - pokazuj tylko na desktop */}
            {!selectedElementId && !isMobile && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm">
                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-full mx-auto mb-2 sm:mb-3 flex items-center justify-center">
                    <span className="text-lg sm:text-xl">👆</span>
                  </div>
                  <h3 className="font-sans text-sm sm:text-base font-semibold text-gray-900 mb-2">Wybierz element</h3>
                  <p className="font-sans text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                    Kliknij na element ogrodzenia w widoku 3D, aby zobaczyć jego właściwości i móc go edytować.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                    <p className="font-sans text-xs text-gray-500 leading-relaxed">
                      💡 Wskazówka: Możesz obracać elementy, zmieniać wysokość słupków i usuwać niepotrzebne elementy.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Zoptymalizowany kalkulator cen i podsumowanie - ukryj na mobile gdy element jest wybrany */}
            {(!selectedElementId || !isMobile) && (
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="font-sans text-sm sm:text-base font-semibold text-gray-900">
                  Kalkulator cen i podsumowanie
                </h3>
              </div>

              {/* Statystyki ogrodzenia - responsive layout */}
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6" style={{ display: 'none' }}>
                {/* Pierwszy rząd - responsive grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <div className="p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-lg text-center min-h-[60px] sm:min-h-[70px] flex flex-col justify-center">
                    <div className="font-sans text-xs text-gray-600 mb-1">Długość</div>
                    <div className="font-sans text-base sm:text-lg font-bold text-gray-900 leading-tight">
                      {state.elements.length > 0 ? 
                        (() => {
                          // 🎯 Oblicz rzeczywistą długość na podstawie wymiarów bloczka
                          if (!state.selectedSystem?.dimensions) {
                            return '0.0'
                          }
                          
                          // Parsuj wymiary bloczka (np. "100×20×30" -> 100 to długość)
                          const dimensions = state.selectedSystem.dimensions.split('×')
                          const blockLengthCm = parseInt(dimensions[0]) || 100 // Długość to pierwszy wymiar
                          const blockLengthM = blockLengthCm / 100 // Konwersja na metry
                          
                          // Oblicz całkowitą długość wszystkich elementów
                          let totalLength = 0
                          state.elements.forEach(element => {
                            if (element.type === 'post') {
                              totalLength += blockLengthM // Słupek = 1 bloczek długości
                            } else if (element.type === 'section') {
                              const multiplier = element.widthMultiplier || 1
                              totalLength += blockLengthM * multiplier // Section 2x = 2 × długość bloczka
                            }
                            // Dla gate/door nie liczymy długości
                          })
                          
                          return totalLength.toFixed(1)
                        })() : '0.0'
                      } m
                    </div>
                  </div>
                  <div className="p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-lg text-center min-h-[60px] sm:min-h-[70px] flex flex-col justify-center">
                    <div className="font-sans text-xs text-gray-600 mb-1">Elementy</div>
                    <div className="font-sans text-base sm:text-lg font-bold text-gray-900 leading-tight">{state.elements.length} szt.</div>
                  </div>
                </div>
                
                {/* Drugi rząd - pełna szerokość */}
                <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-center min-h-[60px] sm:min-h-[70px] flex flex-col justify-center">
                  <div className="font-sans text-xs text-red-600 mb-1">Cena całkowita</div>
                  <div className="font-sans text-lg sm:text-xl font-bold text-red-600 leading-tight">{state.totalPrice.toFixed(0)} zł</div>
                </div>
              </div>

              {/* Lista elementów - kompaktowo */}
              {state.elements.length > 0 && (
                <div className="mb-4 sm:mb-6">
                  <h4 className="font-sans text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">Elementy projektu</h4>
                  <div className="space-y-1 sm:space-y-2">
                    {(() => {
                      const stats = {
                        posts: state.elements.filter(el => el.type === 'post').length,
                        gates: state.elements.filter(el => el.type === 'gate').length,
                        doors: state.elements.filter(el => el.type === 'door').length,
                        sections: state.elements.filter(el => el.type === 'section').length
                      }
                      
                      const activeFenceConfig = state.selectedSystem
                        ? fullFenceConfig || getFenceConfig(state.selectedSystem.id)
                        : null

                      const getPricing = () => {
                        let blockPrice =
                          state.selectedSystem?.blockPrice ??
                          activeFenceConfig?.blockPrice ??
                          150
                        let decorationBlockPrice =
                          state.selectedSystem?.decorationBlockPrice ??
                          activeFenceConfig?.decorationBlockPrice ??
                          blockPrice

                        if (state.selectedColor && activeFenceConfig?.colorPrices?.[state.selectedColor]) {
                          const colorPricing = activeFenceConfig.colorPrices[state.selectedColor]
                          if (colorPricing.blockPrice) blockPrice = colorPricing.blockPrice
                          if (colorPricing.decorationBlockPrice) decorationBlockPrice = colorPricing.decorationBlockPrice
                        }

                        return { blockPrice, decorationBlockPrice }
                      }

                      const pricing = getPricing()

                      const computePostPrice = (element: FenceElement) => {
                        const decorationBlocks = activeFenceConfig?.elements?.post?.decorationBlocksCount ?? 1
                        const defaultHeight = activeFenceConfig?.elements?.post?.defaultHeight ?? 6
                        const heightBlocks = Math.max(element.height ?? defaultHeight, 0)
                        const decorative = Math.min(decorationBlocks, heightBlocks)
                        const regular = Math.max(heightBlocks - decorative, 0)
                        return (regular * pricing.blockPrice) + (decorative * pricing.decorationBlockPrice)
                      }

                      const computeSectionPrice = (element: FenceElement) => {
                        const decorationBlocks = activeFenceConfig?.elements?.section?.decorationBlocksCount ?? 1
                        const defaultHeight = activeFenceConfig?.elements?.section?.defaultHeight ?? 1
                        const heightBlocks = Math.max(element.height ?? defaultHeight, 0)
                        const decorative = Math.min(decorationBlocks, heightBlocks)
                        const regular = Math.max(heightBlocks - decorative, 0)
                        // 🚫 Nie liczymy metalPrice - tylko bloki
                        const basePrice =
                          (regular * pricing.blockPrice) +
                          (decorative * pricing.decorationBlockPrice)
                        return basePrice * (element.widthMultiplier ?? 1)
                      }

                      const computeGatePrice = (element: FenceElement) => {
                        const baseGatePrice =
                          activeFenceConfig?.elements?.gate?.basePrice ??
                          1000
                        return baseGatePrice * (element.widthMultiplier ?? 1)
                      }

                      const computeDoorPrice = (element: FenceElement) => {
                        const baseDoorPrice =
                          activeFenceConfig?.elements?.door?.basePrice ??
                          525
                        return baseDoorPrice * (element.widthMultiplier ?? 1)
                      }

                      const getElementMaterialPrice = (element: FenceElement) => {
                        if (Number.isFinite(element.price)) {
                          return element.price!
                        }
                        if (!activeFenceConfig) {
                          return 0
                        }
                        switch (element.type) {
                          case 'post':
                            return computePostPrice(element)
                          case 'section':
                            return computeSectionPrice(element)
                          case 'gate':
                            return computeGatePrice(element)
                          case 'door':
                            return computeDoorPrice(element)
                          default:
                            return 0
                        }
                      }

                      const getTotalPriceForType = (type: 'post' | 'section' | 'gate' | 'door') => {
                        return state.elements
                          .filter(el => el.type === type)
                          .reduce((sum, el) => sum + getElementMaterialPrice(el), 0)
                      }
                      
                      return [
                        { key: 'posts', label: 'Słupki', count: stats.posts, totalPrice: getTotalPriceForType('post'), showPrice: true },
                        { key: 'gates', label: 'Bramy', count: stats.gates, totalPrice: getTotalPriceForType('gate'), showPrice: false },
                        { key: 'doors', label: 'Furtki', count: stats.doors, totalPrice: getTotalPriceForType('door'), showPrice: false },
                        { key: 'sections', label: 'Przęsła', count: stats.sections, totalPrice: getTotalPriceForType('section'), showPrice: true }
                      ].filter(item => item.count > 0).map(item => {
                        return (
                          <div key={item.key} className="flex justify-between items-center p-2 sm:p-2 bg-gray-50 rounded text-xs sm:text-sm">
                            <span className="font-sans text-gray-700">{item.label}:</span>
                            <div className="text-right">
                              <span className="font-sans font-medium text-gray-900">{item.count} szt.</span>
                              {item.showPrice && (
                                <span className="font-sans text-gray-500 ml-1 sm:ml-2">({item.totalPrice.toFixed(2)} zł)</span>
                              )}
                            </div>
                          </div>
                        )
                      })
                    })()}
                  </div>
                </div>
              )}


              {/* Podgląd oferty */}
              <QuotePreview
                elements={state.elements}
                selectedSystem={state.selectedSystem}
                selectedColor={state.selectedColor}
                totalLength={state.totalLength}
                totalPrice={state.totalPrice}
                className="mb-4"
              />

              {/* Generowanie PDF */}
              <div className="space-y-3">
                <PDFGenerator
                  elements={state.elements}
                  selectedSystem={state.selectedSystem}
                  selectedColor={state.selectedColor}
                  totalLength={state.totalLength}
                  totalPrice={state.totalPrice}
                  onGeneratePDF={() => {
                    console.log('✅ PDF oferty wygenerowany!')
                  }}
                />
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* 🎯 Popup po wyborze ogrodzenia – informacja o wyborze punktu startu */}
      {showStartPointPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-4 sm:p-6 mobile-popup">
            <h3 className="font-sans text-lg font-bold text-gray-900 mb-3 text-center">
              Wybierz punkt startu ogrodzenia
            </h3>
            <p className="font-sans text-sm text-gray-700 mb-4 text-center">
              Kliknij na planszy <span className="font-semibold">3D lub 2D</span>, aby wskazać, w którym miejscu ma się
              zacząć ogrodzenie. Po wybraniu punktu startu możesz dodawać słupki, przęsła, bramy i furtki.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => setShowStartPointPopup(false)}
                className="px-5 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                OK, rozumiem
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Toolbar - Nowy przejrzysty interfejs */}
      {state.selectedSystem && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-300 z-30 shadow-2xl mobile-bottom-toolbar">
          {/* Info Bar */}
          <div className="px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-xs text-gray-600 font-medium">Elementy</div>
                  <div className="text-sm font-bold text-gray-900">{state.elements.length}</div>
                </div>
                <div className="h-8 w-px bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 font-medium">Cena</div>
                  <div className="text-sm font-bold text-red-600">{state.totalPrice.toFixed(0)} zł</div>
                </div>
              </div>
              {state.isClosed && (
                <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                  ✓ Zamknięte
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-3 bg-white">
            <div className="grid grid-cols-4 gap-2">
              {/* Słupek */}
              <button
                onClick={handleMobileAddPost}
                disabled={state.isClosed || !fullFenceConfig}
                className="flex flex-col items-center justify-center p-3 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100 active:bg-blue-200 active:scale-95 transition-all touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mb-1.5 shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </div>
                <span className="text-xs font-bold text-blue-700">Słupek</span>
              </button>

              {/* Przęsło */}
              <button
                onClick={handleMobileAddSection}
                disabled={state.isClosed || !fullFenceConfig || (state.elements.length > 0 && ['section', 'gate', 'door'].includes(state.elements[state.elements.length - 1]?.type || ''))}
                className="flex flex-col items-center justify-center p-3 bg-green-50 border-2 border-green-200 rounded-xl hover:bg-green-100 active:bg-green-200 active:scale-95 transition-all touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mb-1.5 shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </div>
                <span className="text-xs font-bold text-green-700">Przęsło</span>
              </button>

              {/* Brama */}
              <button
                onClick={handleMobileAddGate}
                disabled={state.isClosed || !fullFenceConfig || (state.elements.length > 0 && state.elements[state.elements.length - 1]?.type === 'section')}
                className="flex flex-col items-center justify-center p-3 bg-purple-50 border-2 border-purple-200 rounded-xl hover:bg-purple-100 active:bg-purple-200 active:scale-95 transition-all touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mb-1.5 shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <span className="text-xs font-bold text-purple-700">Brama</span>
              </button>

              {/* Furtka */}
              <button
                onClick={handleMobileAddDoor}
                disabled={state.isClosed || !fullFenceConfig || (state.elements.length > 0 && state.elements[state.elements.length - 1]?.type === 'section')}
                className="flex flex-col items-center justify-center p-3 bg-orange-50 border-2 border-orange-200 rounded-xl hover:bg-orange-100 active:bg-orange-200 active:scale-95 transition-all touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mb-1.5 shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <span className="text-xs font-bold text-orange-700">Furtka</span>
              </button>
            </div>

            {/* Quick Action - Zamknij obwód */}
            {state.elements.length >= 2 && !state.isClosed && (
              <button
                onClick={handleMobileClosePerimeter}
                className="w-full mt-2 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold text-sm shadow-lg hover:from-red-700 hover:to-red-800 active:scale-95 transition-all touch-manipulation flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Zamknij obwód</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Fence Selection Popup */}
      <FenceSelectionPopup
        isOpen={showFencePopup}
        onClose={() => setShowFencePopup(false)}
        onFenceSelect={handleFenceSelect}
        selectedFence={state.selectedSystem || undefined}
      />
    </div>
  )
}

export default FenceConfigurator
